import { Component } from '@angular/core';
import { forkJoin } from 'rxjs';
import { QuotesService } from 'src/app/services/quotes.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { Tooltip } from 'bootstrap';
import { Location } from '@angular/common';
import Swal from 'sweetalert2';
import { Router } from '@angular/router';

@Component({
    selector: 'app-maintenance-supplier',
    templateUrl: './maintenance-supplier.component.html',
    styleUrls: ['./maintenance-supplier.component.css']
})
export class MaintenanceSupplierComponent implements OnInit, AfterViewInit, OnDestroy {
    constructor(
        private quotesService: QuotesService,
        private spinner: NgxSpinnerService,
        private fb: FormBuilder,
        private location: Location,
        private router: Router
    ) { }

    form!: FormGroup;
    dataPaymentType: any = []
    dataBank: any = []
    maintenance_id: any = null
    isExternal: boolean = false
    // Autocomplete state per supplier index
    supplierOptions: any[][] = [];
    showOptions: boolean[] = [];
    private _tooltips: any[] = [];
    dataSupplierComparison: any = [];
    // IMask options
    costMask: any = { mask: Number, scale: 2, thousandsSeparator: '.', radix: ',', normalizeZeros: true, padFractionalZeros: true };
    // No-decimals money mask for refacciones and mano de obra
    amountMask: any = { mask: Number, scale: 0, thousandsSeparator: '.', normalizeZeros: true };
    accountMask: any = '0000 0000 0000 0000 0000';
    ngOnInit(): void {

        let data_location: any = this.location.getState()
        this.maintenance_id = data_location?.data?.maintenance_id
        this.isExternal = data_location?.data?.isExternal
        // Build base form
        this.form = this.fb.group({
            maintenance_id: [this.maintenance_id],
            suppliers: this.fb.array([this.newSupplier(), this.newSupplier()])
        });

        // Load selects data
        this.spinner.show();
        const servicio1$ = this.quotesService.getPaymentType();
        const servicio2$ = this.quotesService.getBank();
        const servicio3$ = this.quotesService.getSupplierComparison(this.maintenance_id);

        forkJoin([servicio1$, servicio2$, servicio3$]).subscribe({
            next: ([resp1, resp2, resp3]) => {
                this.dataPaymentType = resp1;
                this.dataBank = resp2;
                this.dataSupplierComparison = resp3 as any;
                // Si existen comparaciones de proveedores, llenar el formulario
                const r3: any = resp3 as any;
                const list: any[] = Array.isArray(r3) ? r3 : (r3?.data || []);
                if (Array.isArray(list) && list.length > 0) {
                    // Asegurar mínimo 2 filas, o tantas como vengan del backend
                    const needed = Math.max(list.length, 2);
                    const suppliersFA = this.suppliers;
                    while (suppliersFA.length < needed) suppliersFA.push(this.newSupplier());
                    while (suppliersFA.length > needed) suppliersFA.removeAt(suppliersFA.length - 1);

                    // Inicializar estados UI auxiliares
                    this.supplierOptions = new Array(needed).fill([]);
                    this.showOptions = new Array(needed).fill(false);

                    // Mapear campos por cada item
                    list.forEach((item, idx) => {
                        const fg = suppliersFA.at(idx) as FormGroup;
                        const supplier = item?.supplier || {};

                        // Formatear costo como moneda (como en onCostBlur/selectSupplier)
                        const formattedCost: string | null = this.formatCurrency(item?.cost ?? item?.service_cost);

                        // Reconstruir refacciones del proveedor
                        const itemsFA = fg.get('repair_items') as FormArray;
                        itemsFA.clear({ emitEvent: false });
                        (item?.repair_items || []).forEach((r: any) => {
                            const g = this.newRepairItem();
                            g.patchValue({
                                name: r?.name ?? '',
                                quantity: r?.quantity ?? 0,
                                subtotal: this.formatAmount(r?.subtotal)
                            }, { emitEvent: false });
                            itemsFA.push(g, { emitEvent: false });
                        });

                        fg.patchValue({
                            supplier_id: item?.supplier_id ?? supplier?.id ?? null,
                            id: item?.id ?? null,
                            workshop_name: supplier?.name ?? supplier?.workshop_name ?? '',
                            workshop_location: supplier?.direction_address ?? supplier?.workshop_location ?? supplier?.address ?? '',
                            service_cost: formattedCost ?? null,
                            estimated_time: item?.estimated_time ?? '',
                            bank_id: supplier?.bank_id ?? null,
                            account_number: supplier?.account_number ?? '',
                            account_holder: supplier?.account_name ?? '',
                            payment_type_id: item?.payment_type_id ?? supplier?.payment_type_id ?? null,
                            attached_url: item?.attached_url ?? '',
                            labor_cost: item?.labor_cost != null ? this.formatAmount(item.labor_cost) : null,
                            labor_description: item?.labor_description ?? ''
                        }, { emitEvent: false });
                    });
                }
                this.spinner.hide();
            },
            error: () => {
                this.spinner.hide();
            }
        });
    }

    ngAfterViewInit(): void {
        // Initialize Bootstrap tooltips for elements with data-bs-toggle="tooltip"
        const tooltipTriggerList = Array.from(document.querySelectorAll('[data-bs-toggle="tooltip"]')) as HTMLElement[];
        this._tooltips = tooltipTriggerList.map((el) => new (Tooltip as any)(el));
    }

    ngOnDestroy(): void {
        // Dispose tooltips to avoid memory leaks
        this._tooltips.forEach(t => t.dispose());
        this._tooltips = [];
    }

    // Handle evidence file selection for supplier i (single PDF only)
    onFilesSelected(event: Event, i: number): void {
        const input = event.target as HTMLInputElement;
        const file = (input.files && input.files[0]) ? input.files[0] : undefined;
        const ctrl = this.suppliers.at(i).get('evidences');
        if (!ctrl) return;
        if (file) {
            if (file.type !== 'application/pdf') {
                // Not a PDF, ignore selection
                console.warn('Solo se permiten archivos PDF.');
            } else {
                // Replace any existing file with the selected one
                ctrl.setValue([file]);
                ctrl.markAsDirty();
                ctrl.updateValueAndValidity({ onlySelf: true });
            }
        }
        // reset input so same file can be selected nuevamente
        input.value = '';
    }

    removeEvidence(i: number, fileIndex: number = 0): void {
        const ctrl = this.suppliers.at(i).get('evidences');
        if (!ctrl) return;
        ctrl.setValue([]);
        ctrl.markAsDirty();
        ctrl.updateValueAndValidity({ onlySelf: true });
    }

    // Remove existing attached URL evidence so user can upload a new file
    removeAttachedUrl(i: number): void {
        const ctrl = this.suppliers.at(i).get('attached_url');
        if (!ctrl) return;
        ctrl.setValue('');
        ctrl.markAsDirty();
        ctrl.updateValueAndValidity({ onlySelf: true });
    }

    // Validator: requires at least one file in evidences control
    private filesRequiredValidator = (control: AbstractControl): ValidationErrors | null => {
        const val = control.value;
        if (val && Array.isArray(val) && val.length > 0) return null;
        return { required: true };
    }

    evidenceFile(i: number): File | null {
        const ctrl = this.suppliers.at(i).get('evidences');
        const list: File[] = (ctrl?.value || []) as File[];
        return list && list.length ? list[0] : null;
    }

    openEvidence(i: number): void {
        const file = this.evidenceFile(i);
        if (!file) return;
        const url = URL.createObjectURL(file);
        window.open(url, '_blank');
        // Revoke after a short delay
        setTimeout(() => URL.revokeObjectURL(url), 5000);
    }

    get suppliers(): FormArray {
        return this.form.get('suppliers') as FormArray;
    }

    newSupplier(): FormGroup {
        return this.fb.group({
            supplier_id: [null],
            id: [null],
            workshop_name: ['', [Validators.required]],
            workshop_location: ['', [Validators.required]],
            service_cost: [null, [Validators.required]],
            estimated_time: ['', [Validators.required]],
            bank_id: [null, [Validators.required]],
            account_number: ['', [Validators.required]],
            account_holder: ['', [Validators.required]],
            payment_type_id: [null, [Validators.required]],
            // Existing evidence URL from backend (counts as evidence present)
            attached_url: [''],
            // Evidences are optional for save, required only when requesting authorization
            evidences: [[]],
            // Optional labor and repair items per supplier
            labor_cost: [null],
            labor_description: [''],
            repair_items: this.fb.array([])
        });
    }

    newRepairItem(): FormGroup {
        return this.fb.group({
            name: ['', [Validators.required]],
            // Starts at 0 like the mockup; min(1) still required to save
            quantity: [0, [Validators.required, Validators.min(1)]],
            subtotal: [null, [Validators.required]]
        });
    }

    repairItems(i: number): FormArray {
        return this.suppliers.at(i).get('repair_items') as FormArray;
    }

    addRepairItem(i: number): void {
        this.repairItems(i).push(this.newRepairItem());
    }

    removeRepairItem(i: number, j: number): void {
        this.repairItems(i).removeAt(j);
    }

    stepQuantity(i: number, j: number, delta: number): void {
        const ctrl = this.repairItems(i).at(j).get('quantity');
        const current = Number(ctrl?.value) || 0;
        ctrl?.setValue(Math.max(0, current + delta));
        ctrl?.markAsDirty();
    }

    getRepairTotal(i: number): number {
        return this.repairItems(i).controls.reduce((acc, c) => {
            const v = this.parseCurrency(c.get('subtotal')?.value);
            return acc + (v ?? 0);
        }, 0);
    }

    // Normalize a formatted currency value to number, handling both es-CO (1.234,56) and plain (1234.56)
    parseCurrency(value: any): number | null {
        if (value === null || value === undefined || value === '') return null;
        if (typeof value === 'number') return isNaN(value) ? null : value;
        let str = String(value).trim().replace(/[^0-9.,-]/g, '');
        if (str === '') return null;
        const hasComma = str.includes(',');
        const hasDot = str.includes('.');
        if (hasComma && hasDot) {
            // Likely formatted currency: dots as thousands, comma as decimal
            str = str.replace(/\./g, '').replace(/,/g, '.');
        } else if (hasComma && !hasDot) {
            // Only comma present: treat as decimal comma
            str = str.replace(/,/g, '.');
        } else if (str.match(/^-?\d{1,3}(\.\d{3})+$/)) {
            // Dot-only with thousands grouping (mask format): 1.000 = mil
            str = str.replace(/\./g, '');
        } else {
            // Only dot or only digits: already decimal dot, ensure single dot
            const parts = str.split('.');
            if (parts.length > 2) {
                str = parts[0] + '.' + parts.slice(1).join('');
            }
        }
        const parsed = parseFloat(str);
        return isNaN(parsed) ? null : parsed;
    }

    formatCurrency(value: any): string | null {
        const num = typeof value === 'number' ? value : Number(value);
        if (value === null || value === undefined || value === '' || isNaN(num)) return null;
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(num);
    }

    // Plain amount without symbol or decimals (es-CO grouping): 1000 -> "1.000"
    formatAmount(value: any): string | null {
        const num = typeof value === 'number' ? value : Number(value);
        if (value === null || value === undefined || value === '' || isNaN(num)) return null;
        return new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 }).format(num);
    }

    addSupplier(): void {
        this.suppliers.push(this.newSupplier());
    }

    removeSupplier(index: number): void {
        // Only allow removal when there are 3 or more suppliers
        if (this.suppliers.length > 2) {
            this.suppliers.removeAt(index);
        }
    }

    // --- Autocomplete: workshop_name ---
    onWorkshopInput(i: number, event: Event): void {
        const input = event.target as HTMLInputElement;
        const q = (input.value || '').trim();
        if (q.length < 2) {
            this.supplierOptions[i] = [];
            this.showOptions[i] = false;
            return;
        }
        this.quotesService.getSuppliers(q).subscribe({
            next: (resp: any) => {
                this.supplierOptions[i] = Array.isArray(resp) ? resp : (resp?.data || []);
                this.showOptions[i] = (this.supplierOptions[i].length > 0);
            },
            error: () => {
                this.supplierOptions[i] = [];
                this.showOptions[i] = false;
            }
        });
    }

    selectSupplier(i: number, supplier: any): void {
        const fg = this.suppliers.at(i) as FormGroup;
        // Always set workshop name if present
        if(supplier?.id){
            fg.get('supplier_id')?.setValue(supplier.id);
        }
        if (supplier?.name || supplier?.workshop_name) {
            fg.get('workshop_name')?.setValue(supplier.name ?? supplier.workshop_name);
        }
        // Location
        if (supplier?.direction_address || supplier?.workshop_location || supplier?.address) {
            fg.get('workshop_location')?.setValue(supplier.direction_address ?? supplier.workshop_location ?? supplier.address);
        }
        // Optional banking/payment fields if API provides them
        if (supplier?.bank_id !== undefined) fg.get('bank_id')?.setValue(supplier.bank_id);
        if (supplier?.account_number !== undefined) fg.get('account_number')?.setValue(supplier.account_number);
        if (supplier?.account_name !== undefined) fg.get('account_holder')?.setValue(supplier.account_name);

        // Hide dropdown
        this.showOptions[i] = false;
        this.supplierOptions[i] = [];
    }

    // Display index as letter: 0->A, 1->B, 2->C, ...
    indexToLetter(i: number): string {
        const base = 'A'.charCodeAt(0);
        // Support beyond Z using AA, AB if needed
        let n = i;
        let result = '';
        do {
            result = String.fromCharCode(base + (n % 26)) + result;
            n = Math.floor(n / 26) - 1;
        } while (n >= 0);
        return result;
    }

    // Mask: account number as #### #### #### ####
    onAccountNumberInput(i: number, event: Event): void {
        const input = event.target as HTMLInputElement;
        const digits = (input.value || '').replace(/\D+/g, '').slice(0, 20); // up to 20 digits
        const groups = digits.match(/.{1,4}/g) || [];
        const masked = groups.join(' ');
        this.suppliers.at(i).get('account_number')?.setValue(masked, { emitEvent: false });
    }

    // Cost input behavior: sanitize while typing; no currency symbol until blur
    onCostInput(i: number, event: Event): void {
        const input = event.target as HTMLInputElement;
        // Keep only digits and one decimal separator (comma or dot), max 2 decimals
        let val = (input.value || '').replace(/[^0-9.,]/g, '');
        // Normalize multiple separators to a single '.'
        val = val.replace(/,/g, '.');
        const parts = val.split('.');
        if (parts.length > 1) {
            parts[1] = parts[1].slice(0, 2);
            val = parts[0] + '.' + parts[1];
        }
        // Remove leading zeros except before decimal
        val = val.replace(/^0+(\d)/, '$1');
        this.suppliers.at(i).get('service_cost')?.setValue(val, { emitEvent: false });
    }

    onCostFocus(i: number): void {
        const ctrl = this.suppliers.at(i).get('service_cost');
        const raw = ctrl?.value;
        if (typeof raw === 'string' && raw.trim() !== '') {
            // Unformat currency to plain number
            const val = raw.replace(/[^0-9.,-]/g, '').replace(/,/g, '.');
            ctrl?.setValue(val, { emitEvent: false });
        }
    }

    onCostBlur(i: number): void {
        const ctrl = this.suppliers.at(i).get('service_cost');
        let val: any = ctrl?.value;
        if (val === null || val === undefined || val === '') return;
        if (typeof val === 'string') {
            val = parseFloat(val.replace(/,/g, '.'));
        }
        const formatted = this.formatCurrency(val);
        if (formatted !== null) {
            ctrl?.setValue(formatted, { emitEvent: false });
        }
    }

    // At least one supplier complete/valid
    hasAtLeastOneValidSupplier(): boolean {
        return this.suppliers.controls.some(ctrl => ctrl.valid);
    }

    // At least two suppliers complete/valid
    hasAtLeastTwoValidSuppliers(): boolean {
        return this.suppliers.controls.filter(ctrl => ctrl.valid).length >= 2;
    }

    // Ready to request authorization when at least two valid suppliers AND each valid supplier has evidence (file or attached_url)
    canRequestAuthorization(): boolean {
        const valid = this.suppliers.controls
            .map((ctrl, idx) => ({ ctrl, idx }))
            .filter(({ ctrl }) => ctrl.valid);
        if (valid.length < 2) return false;
        // For each valid supplier, require either a file or an attached_url
        for (const { idx } of valid) {
            const ev = this.suppliers.at(idx).get('evidences')?.value as any[];
            const hasFile = Array.isArray(ev) && ev.length > 0;
            const attached = (this.suppliers.at(idx).get('attached_url')?.value || '').toString().trim();
            const hasAttached = attached !== '';
            if (!(hasFile || hasAttached)) return false;
        }
        return true;
    }

    submit(type: number): void {
        if (!this.hasAtLeastOneValidSupplier()) {
            this.form.markAllAsTouched();
            return;
        }
        // Build cleaned payload: parse cost and strip account number mask
        const raw = this.form.value as { suppliers: any[] };
        // Include only valid suppliers
        const validIndices = this.suppliers.controls
            .map((ctrl, idx) => ({ ctrl, idx }))
            .filter(({ ctrl }) => ctrl.valid)
            .map(({ idx }) => idx);
        // If requesting authorization, require evidence for each valid supplier
        if (type === 2) {
            const missingEvidenceIdx = validIndices.find((idx) => {
                const ev = this.suppliers.at(idx).get('evidences')?.value as any[];
                const attached = (this.suppliers.at(idx).get('attached_url')?.value || '').toString().trim();
                const hasFile = Array.isArray(ev) && ev.length > 0;
                const hasAttached = attached !== '';
                return !(hasFile || hasAttached);
            });
            if (missingEvidenceIdx !== undefined) {
                // Mark evidence control as touched to show UI errors if any and stop
                this.suppliers.at(missingEvidenceIdx).get('evidences')?.markAsTouched();
                Swal.fire({
                    icon: 'warning',
                    title: 'Evidencia requerida',
                    text: 'Debes adjuntar la evidencia (PDF) o contar con una evidencia existente para solicitar autorización.'
                });
                return;
            }
        }
        const cleanedSuppliers = raw.suppliers
            .filter((_, idx) => validIndices.includes(idx))
            .map((s) => {
            // Normalize service_cost from formatted currency to number
            const cost = typeof s.service_cost === 'string' ? this.parseCurrency(s.service_cost) : s.service_cost;
            // Normalize account number: keep only digits
            let account = s.account_number;
            if (typeof account === 'string') {
                account = account.replace(/\D+/g, '');
            }
            // Ensure estimated_time is a string as required by backend
            const estimated_time = (s.estimated_time === null || s.estimated_time === undefined)
                ? ''
                : String(s.estimated_time);
            // Map attached_url to quotation_url for backend when no new file is uploaded
            const quotation_url = s.quotation_url ?? s.attached_url ?? '';
            // Repair items and labor as plain numbers
            const repair_items = (s.repair_items || []).map((r: any) => ({
                name: r.name,
                quantity: Number(r.quantity),
                subtotal: this.parseCurrency(r.subtotal)
            }));
            const labor_cost = this.parseCurrency(s.labor_cost);
            return {
                ...s,
                service_cost: cost,
                account_number: account,
                estimated_time,
                quotation_url,
                repair_items,
                labor_cost
            };
        });
        const payload = { type, maintenance_request_id: this.maintenance_id, suppliers: cleanedSuppliers };
        this.spinner.show();
        this.quotesService.addSupplier(payload).subscribe({
            next: (resp) => {
                
            },
            error: () => {
                this.spinner.hide();
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Ocurrio un error al guardar el registro',
                })
            },
            complete: () => {
                this.spinner.hide();
                Swal.fire({
                    icon: 'success',
                    title: 'Exitoso',
                    text: type === 1 ? 'Se ha guardado el registro de forma exitosa' : 'Se ha enviado la solicitud de forma exitosa',
                }).then(() => {
                    this.router.navigate(['/maintenance/list'])
                })
            }
        });
        // TODO: Integrate with backend endpoint for saving suppliers when available
    }
}
